import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import AuraControlledAutocomplete from "app/main/apps/common/AuraControlledAutocomplete";
import { preventNegativeValues } from "app/main/apps/common/AuraFunctions";
import { useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { getShippingCarriers } from "../../store/shipmentOrderSlice";

function ShipmentFormContent(props) {
  const methods = useFormContext();
  const { control, formState, setValue, reset, watch } = methods;
  const shipmentTypeNames = useSelector(
    ({ backofficeApp }) => backofficeApp?.shipmentOrder?.shipmentTypeNames
  );
  const shippingCarrierNames = useSelector(
    ({ backofficeApp }) => backofficeApp?.shipmentOrder?.shippingCarrierNames
  );
  const dispatch = useDispatch();
  const { errors } = formState;
  const shipmentType = watch("shipment_type");
  // PGSS-5490: the same fields, laid out for a 320px side panel instead of a dialog. One column,
  // tighter gaps -- the row layout below puts three thirds side by side and is unusable there.
  const dense = !!props?.dense;
  const rowClass = dense ? "flex flex-col w-full" : "flex flex-col md:flex-row w-full gap-10";
  const cellClass = dense ? "flex w-full" : "flex w-1/3 ";
  const halfCellClass = dense ? "flex w-full" : "flex w-1/2 ";
  const fieldMargin = dense ? "mb-16" : "mb-24";

  useEffect(() => {
    // The dialog is created fresh each time it opens, so it clears on mount. The side panel keeps
    // one form for the life of the screen -- clearing there would wipe what was just typed.
    if (!dense) {
      reset();
    }
  }, []);

  useEffect(() => {
    if (props?.type === 'edit' && !shippingCarrierNames?.length) {
      dispatch(getShippingCarriers());
    }
  }, [props?.type]);

  useEffect(() => {
    const handleWheel = (e) => {
      if (e.target.type === "number") {
        e.target.blur();
      }
    };
    document.addEventListener("wheel", handleWheel, { passive: true });
    return () => {
      document.removeEventListener("wheel", handleWheel);
    };
  }, []);

  return (
    <>
      <div className={rowClass}>
        <div className={cellClass}>
          <Controller
            control={control}
            name="schedule"
            render={({ field }) => (
              <TextField
                {...field}
                className={fieldMargin}
                label="Shipment Date *"
                id="schedule"
                type="date"
                variant="outlined"
                fullWidth
                size="small"
                InputLabelProps={{
                  shrink: true,
                }}
                error={!!errors.schedule}
                helperText={errors?.schedule?.message}
              />
            )}
          />
        </div>
        <div className={cellClass}>
          <AuraControlledAutocomplete
            labelName="Shipment Type"
            control={control}
            controlName="shipment_type"
            inputValues={shipmentTypeNames || []}
            required
            error={!!errors.shipment_type}
            defaultValue=""
            hideClearable
            helperText={errors?.shipment_type?.message}
            className={
              errors && errors?.shipment_type?.message
                ? "mt-0 mb-0"
                : "mt-0 mb-16"
            }
            onUpdate={(e, value) => {
              if(value !== 'LTL') {
                setValue("pallet_count", '');
              }
            }}
          />
        </div>
        {shipmentType === "LTL" && (
          <div className={cellClass}>
            <Controller
              control={control}
              name="pallet_count"
              render={({ field }) => (
                <>
                  <TextField
                    {...field}
                    value={field.value}
                    type="number"
                    label="No of Pallets"
                    variant="outlined"
                    className="p-0"
                    size="small"
                    fullWidth
                    onKeyDown={preventNegativeValues}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                    }}
                    error={!!errors.pallet_count}
                    helperText={errors?.pallet_count?.message}
                    required={shipmentType === "LTL"}
                  />
                </>
              )}
            />
          </div>
        )}
      </div>
      {/* Carrier + tracking number are only editable on an existing shipment: at creation time the
          freight has not been tendered yet, so no PRO number exists.
          NOTE: this dialog is opened by the SHIPMENT_EDIT action, which workflow_actions wires only to
          SHIPMENT_REJECTED — so today these fields are reachable here for rejected shipments only. The
          dispatch dialog is the capture point for the normal flow. */}
      {props?.type === 'edit' && (
        <div className={rowClass}>
          <div className={halfCellClass}>
            <AuraControlledAutocomplete
              labelName="Carrier"
              control={control}
              controlName="carrier"
              inputValues={shippingCarrierNames || []}
              defaultValue=""
              className="mt-0 mb-24"
              error={!!errors.carrier}
              helperText={errors?.carrier?.message}
            />
          </div>
          <div className={halfCellClass}>
            <Controller
              control={control}
              name="tracking_number"
              render={({ field }) => (
                <TextField
                  {...field}
                  className={fieldMargin}
                  label="Tracking / PRO Number"
                  id="tracking_number"
                  variant="outlined"
                  fullWidth
                  size="small"
                  error={!!errors.tracking_number}
                  helperText={errors?.tracking_number?.message}
                />
              )}
            />
          </div>
        </div>
      )}
      <div className={dense ? "flex w-full mb-8" : "flex w-full mb-16"}>
        <Controller
          control={control}
          name="is_priority"
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  size="small"
                />
              }
              label="Priority"
            />
          )}
        />
      </div>
      <div className={dense ? "flex flex-col w-full" : "flex flex-col md:flex-row w-full"}>
        <div className="flex w-full ">
          <Controller
            control={control}
            name="note"
            render={({ field }) => (
              <TextField
                {...field}
                className={fieldMargin}
                label={props?.type === 'edit' ? "Notes" : "Notes to Customer"}
                id="note"
                variant="outlined"
                fullWidth
                size="small"
                multiline
                rows={dense ? 3 : 5}
                error={!!errors.note}
                helperText={errors?.note?.message}
              />
            )}
          />
        </div>
      </div>
    </>
  );
}

export default ShipmentFormContent;
